import Header from '@/components/common/Header';
import DashboardCard from './component/DashboardCard';
import { useState } from 'react';
import { DateRange } from '@/components/common/DateRangePicker';
import {
  useGetUserManagementQuery,
  useGetBalanceSummaryQuery,
  useGetAgentPlayerBalanceSummaryQuery,
  useGetBusinessAnalyticsQuery,
} from '@/store/api/dashboardApi';
import { formatNumberWithCommas } from '@/utils';
import dayjs from 'dayjs';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

const BASE_ICON_URL = '/images/dashboardTwo/';

const last7Days = (): DateRange => {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 6);
  from.setHours(0, 0, 0, 0);
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  return { from, to };
};

const DashboardPageTwo = () => {
  const { isAgent } = useRoleCheck();
  const referralCode = useSelector((state: RootState) => state.user?.data?.referralCode);
  const [selectedRange, setSelectedRange] = useState<DateRange>(last7Days);
  const [appliedRange, setAppliedRange] = useState<DateRange>(last7Days);

  const [balanceDate, setBalanceDate] = useState<Date | undefined>(undefined);

  const dateParams = {
    fromDate: appliedRange.from ? dayjs(appliedRange.from).startOf('day').toISOString() : undefined,
    toDate: appliedRange.to ? dayjs(appliedRange.to).endOf('day').toISOString() : undefined,
  };

  const balanceDateParams = balanceDate
    ? {
        fromDate: dayjs(balanceDate).startOf('day').toISOString(),
        toDate: dayjs(balanceDate).endOf('day').toISOString(),
      }
    : dateParams;

  const {
    data: userMgmt,
    refetch: refetchUserManagement,
    isFetching: isUserManagementFetching,
  } = useGetUserManagementQuery(dateParams);

  const {
    data: balanceData,
    refetch: refetchBalanceSummary,
    isFetching: isBalanceSummaryFetching,
  } = useGetBalanceSummaryQuery(balanceDateParams);

  const {
    data: agentBalanceData,
    refetch: refetchAgentBalanceSummary,
    isFetching: isAgentBalanceSummaryFetching,
  } = useGetAgentPlayerBalanceSummaryQuery(balanceDateParams);

  const {
    data: businessData,
    refetch: refetchBusinessAnalytics,
    isFetching: isBusinessAnalyticsFetching,
  } = useGetBusinessAnalyticsQuery(dateParams);

  const handleRefresh = async () => {
    await Promise.all([
      refetchUserManagement(),
      refetchBalanceSummary(),
      refetchAgentBalanceSummary(),
      refetchBusinessAnalytics(),
    ]);
  };

  const isRefreshing =
    isUserManagementFetching ||
    isBalanceSummaryFetching ||
    isAgentBalanceSummaryFetching ||
    isBusinessAnalyticsFetching;

  const fmt = (val?: number) => formatNumberWithCommas(Number(val ?? 0));

  const dashboardData = [
    {
      id: 1,
      title: 'Net Gaming Revenue (NGR)',
      number: fmt(businessData?.businessAnalytics?.ngr),
      growth: '',
      icon: `${BASE_ICON_URL}ngr.svg`,
      col: 2,
      onlyOwner: true,
    },
    {
      id: 2,
      title: 'Gross Gaming Revenue (GGR)',
      number: fmt(businessData?.businessAnalytics?.ggr),
      growth: '',
      icon: `${BASE_ICON_URL}ggr.svg`,
      col: 2,
      onlyOwner: true,
    },
    {
      id: 3,
      title: 'Active Players',
      number: fmt(userMgmt?.userManagement.activeUser),
      growth: '',
      icon: `${BASE_ICON_URL}activeUsers.svg`,
    },
    {
      id: 4,
      title: 'New Players',
      number: fmt(userMgmt?.userManagement.newUser),
      growth: '',
      icon: `${BASE_ICON_URL}user.svg`,
    },
    {
      id: 5,
      title: 'Players Balance',
      number: isAgent
        ? fmt(agentBalanceData?.playerBalance)
        : fmt(balanceData?.balanceSummary.playerBalance),
      growth: '',
      icon: `${BASE_ICON_URL}playersBalance.svg`,
      showDateFilter: true,
    },
    {
      id: 6,
      title: 'Total Deposit',
      number: fmt(businessData?.businessAnalytics.totalDeposits),
      growth: '',
      icon: `${BASE_ICON_URL}totalDeposit.svg`,
    },
    {
      id: 7,
      title: 'Total Withdraw',
      number: fmt(businessData?.businessAnalytics.totalWithdraws),
      growth: '',
      icon: `${BASE_ICON_URL}totalWithdraw.svg`,
    },
    {
      id: 8,
      title: 'New Player Deposit',
      number: fmt(businessData?.businessAnalytics.newPlayerDeposit),
      growth: '+12.5%',
      icon: `${BASE_ICON_URL}newPlayerD.svg`,
    },
    {
      id: 9,
      title: 'New Player Withdraw',
      number: fmt(businessData?.businessAnalytics.newPlayerWithdraw),
      growth: '+12.5%',
      icon: `${BASE_ICON_URL}newPlayerW.svg`,
    },
    {
      id: 10,
      title: 'Total Players',
      number: fmt(userMgmt?.userManagement.user),
      growth: '',
      icon: `${BASE_ICON_URL}threeUsers.svg`,
    },
    {
      id: 11,
      title: 'Idle Players',
      number: fmt(userMgmt?.userManagement.idleUser),
      growth: '',
      icon: `${BASE_ICON_URL}idleUser.svg`,
    },
    {
      id: 12,
      title: 'Losing Commission Distributed',
      number: fmt(businessData?.businessAnalytics?.lossCommissionAmount),
      growth: '+12.5%',
      icon: `${BASE_ICON_URL}losingCommission.svg`,
    },
    {
      id: 13,
      title: 'Total Agents',
      number: fmt(userMgmt?.userManagement.agent),
      growth: '',
      icon: `${BASE_ICON_URL}totalAgents.svg`,
    },
    {
      id: 14,
      title: 'Bonus Distributed',
      number: fmt(businessData?.businessAnalytics.bonusDistributed),
      growth: '+12.5%',
      icon: `${BASE_ICON_URL}bonusDistributed.svg`,
    },
  ].filter((card) => (isAgent ? !card.onlyOwner : true));

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <Header
        title="Dashboard"
        agentCode={isAgent ? (referralCode ?? null) : null}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        showDateRangePicker
        dateRangeValue={selectedRange}
        onDateRangeChange={setSelectedRange}
        onDateRangeApply={() => {
          setAppliedRange(selectedRange);
        }}
      />

      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {dashboardData.map((item) => (
          <DashboardCard
            key={item.id}
            icon={item.icon}
            title={item.title}
            number={item.number}
            growthPercent={item.growth}
            columns={item.col ?? 1}
            showDateFilter={item.showDateFilter}
            selectedDate={item.id === 5 ? balanceDate : undefined}
            onDateChange={item.id === 5 ? setBalanceDate : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default DashboardPageTwo;
